/**
 * Navigation is data, not markup.
 *
 * The client console has sixteen sections (BETA-PLAN workstream D). They are
 * grouped the way the prototype groups them — the daily loop first, then the
 * control surfaces, then the evidence layer, then administration — because
 * that ordering is the product's argument about what an agent owner does all
 * day, and reordering it would quietly change the pitch.
 *
 * Each item carries the capability that gates it. The nav renders what the
 * signed-in person may actually open; it never shows a door that returns 403.
 */

import type { ComponentType, SVGProps } from 'react';
import type { Capability } from '@otto-iq/shared';
import {
  IconApprovals,
  IconArtifacts,
  IconAudit,
  IconAutomations,
  IconBench,
  IconBilling,
  IconChat,
  IconFleet,
  IconHelp,
  IconIncidents,
  IconIntegrations,
  IconShield,
  IconMemory,
  IconOrgs,
  IconOverview,
  IconPeople,
  IconReleases,
  IconSessions,
  IconSettings,
  IconSkills,
  IconUsage,
  IconUtilisation,
} from '../components/icons';

export type NavIcon = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

export interface NavItem {
  to: string;
  label: string;
  icon: NavIcon;
  /** Capability required to see the item at all. */
  capability?: Capability;
  /** Exact-match highlighting, for index routes. */
  end?: boolean;
  /** Marked as unimplemented in the nav so nobody demos it by accident. */
  pending?: boolean;
  /**
   * Switched off entirely: shown greyed, not a link, and its route refuses to
   * render.
   *
   * Different from `pending`, which still navigates — that marks a page whose
   * *content* is partly unbuilt. This is for a page that works but is not part
   * of what the product currently offers, so the nav should not invite a click
   * and a typed URL should not get there either.
   */
  disabled?: boolean;
  /** Shown on hover and to assistive tech, so "why not?" has an answer. */
  disabledReason?: string;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const CLIENT_NAV: NavGroup[] = [
  {
    label: 'Every day',
    items: [
      { to: '/', label: 'Overview', icon: IconOverview, capability: 'overview.read', end: true },
      { to: '/chat', label: 'Chat', icon: IconChat, capability: 'chat.send' },
      { to: '/sessions', label: 'Sessions', icon: IconSessions, capability: 'sessions.read' },
      // `pending` here means "part of this is not built" — the email half. The
      // page itself works; see the PartialFeature banner on it.
      {
        to: '/approvals',
        label: 'Approvals',
        icon: IconApprovals,
        capability: 'approvals.read',
        pending: true,
      },
    ],
  },
  {
    label: 'What it can do',
    items: [
      {
        to: '/automations',
        label: 'Automations',
        icon: IconAutomations,
        capability: 'schedules.read',
        pending: true,
      },
      { to: '/skills', label: 'Skills', icon: IconSkills, capability: 'skills.read' },
      // No longer pending: a real connector ships, and the page onboards it
      // end to end — credentials, config, per-agent access and a connection
      // test that makes an authenticated call.
      {
        to: '/integrations',
        label: 'Integrations',
        icon: IconIntegrations,
        capability: 'integrations.read',
      },
      /*
       * Guardrails sits after Integrations deliberately: the group now reads
       * what starts it → what it knows → what it can reach → what stops it.
       * It was tab five of Automations, where nobody looked for a spending
       * threshold, which is the whole reason it moved.
       */
      {
        to: '/guardrails',
        label: 'Guardrails',
        icon: IconShield,
        capability: 'policy.read',
      },
      {
        to: '/memory',
        label: 'Memory',
        icon: IconMemory,
        capability: 'memories.read',
      },
    ],
  },
  {
    label: 'What it did',
    items: [
      { to: '/audit', label: 'Audit log', icon: IconAudit, capability: 'audit.read' },
      {
        to: '/artifacts',
        label: 'Artifacts',
        icon: IconArtifacts,
        capability: 'artifacts.read',
      },
      {
        to: '/usage',
        label: 'Usage & data',
        icon: IconUsage,
        capability: 'usage.read',
      },
      {
        to: '/utilisation',
        label: 'Utilisation',
        icon: IconUtilisation,
        capability: 'utilisation.read',
      },
    ],
  },
  {
    label: 'Setup',
    items: [
      { to: '/people', label: 'People', icon: IconPeople, capability: 'people.read' },
      {
        to: '/agent-settings',
        label: 'Agent settings',
        icon: IconSettings,
        capability: 'agents.read',
      },
      {
        to: '/test-bench',
        label: 'Test bench',
        icon: IconBench,
        capability: 'settings.read',
        // Off for now. It works, but its isolation depends on a `sandboxSafe`
        // connector to swap in, and there is none registered: `demo-ledger`
        // left with the seeded integrations and ConnectWise is deliberately
        // not sandbox-safe. A test bench with no callable tools demonstrates
        // nothing, so it is switched off rather than left to disappoint.
        disabled: true,
        disabledReason: 'Not available yet — no sandbox-safe connector is registered.',
      },
      // No capability, deliberately. Help is the one section where a viewer who
      // cannot open anything else still has something to read, and gating it
      // would mean inventing a `help.read` capability in the shared package for
      // an API surface that does not exist.
      { to: '/help', label: 'Help', icon: IconHelp },
    ],
  },
];

/** Flat list of every client section — used by the router and by tests. */
export const CLIENT_NAV_ITEMS: NavItem[] = CLIENT_NAV.flatMap((group) => group.items);

export const MASTER_NAV: NavItem[] = [
  { to: '/master', label: 'Fleet Health', icon: IconFleet, end: true },
  { to: '/master/orgs', label: 'Orgs & agents', icon: IconOrgs },
  { to: '/master/incidents', label: 'Incidents', icon: IconIncidents },
  { to: '/master/releases', label: 'Releases', icon: IconReleases },
  { to: '/master/billing', label: 'Billing', icon: IconBilling },
  { to: '/master/settings', label: 'Settings', icon: IconSettings },
];

/**
 * Is this client section switched off?
 *
 * Read by the sidebar *and* by the route table, so a greyed entry and an
 * unreachable page are the same fact rather than two edits that have to agree.
 */
export function isNavDisabled(path: string): boolean {
  return CLIENT_NAV.flatMap((group) => group.items).some(
    (item) => item.to === path && item.disabled === true,
  );
}
